import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Heart, Shield, User, Crown, MapPin } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";
import { isStructuredTurnMode, normalizeTurnMode } from "@/lib/normalize-turn-mode";

interface PartyMember {
  id: string;
  user_id: string;
  character_name: string;
  character_data: any;
  is_host: boolean;
}

interface PartyPanelProps {
  players: PartyMember[];
  currentUserId: string | null;
  currentPlayerId: string | null;
  turnMode: string;
}

const VITAL_KEYS = ["hp", "health", "mana", "mp", "stamina", "sp"];

export function PartyPanel({ players, currentUserId, currentPlayerId, turnMode }: PartyPanelProps) {
  const { t } = useLocale();
  if (players.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground/40">
        <User className="w-10 h-10" />
        <p className="text-xs">{t.play_no_party}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
        {t.play_party} ({players.length})
      </div>
      {players.map((p) => {
        const isMe = p.user_id === currentUserId;
        const isTurn = isStructuredTurnMode(normalizeTurnMode(turnMode)) && p.id === currentPlayerId;
        const cd = p.character_data || {};
        const stats = cd.stats || {};
        const conditions = cd.conditions || [];

        // Extract vitals
        const vitals: { key: string; value: number; max: number }[] = [];
        for (const vk of VITAL_KEYS) {
          if (stats[vk] !== undefined) {
            vitals.push({
              key: vk,
              value: Number(stats[vk]) || 0,
              max: Number(stats[`${vk}_max`] || stats[`max_${vk}`]) || 100,
            });
          }
        }

        return (
          <div
            key={p.id}
            className={cn(
              "rounded-lg border p-3 space-y-2 transition-colors",
              isTurn ? "border-primary/50 bg-primary/5" : "border-border/50 bg-muted/10",
              isMe && "ring-1 ring-primary/20",
            )}
          >
            {/* Header */}
            <div className="flex items-center gap-2">
              {p.is_host ? (
                <Crown className="w-3.5 h-3.5 text-primary shrink-0" />
              ) : (
                <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              )}
              <span className={cn("text-sm font-medium truncate", isMe ? "text-foreground" : "text-foreground/80")}>
                {cd.name || p.character_name || "Unnamed"}
              </span>
              {isMe && (
                <Badge variant="outline" className="text-[9px] h-4 px-1 ml-auto shrink-0">{t.play_you}</Badge>
              )}
              {isTurn && (
                <Badge variant="default" className="text-[9px] h-4 px-1 shrink-0 animate-pulse">{t.play_turn}</Badge>
              )}
            </div>

            {/* Description */}
            {cd.description && (
              <p className="text-[10px] text-muted-foreground italic line-clamp-2">{cd.description}</p>
            )}

            {/* Vital bars */}
            {vitals.length > 0 && (
              <div className="space-y-1">
                {vitals.map((v) => {
                  const percent = Math.min(100, Math.max(0, (v.value / v.max) * 100));
                  const isLow = percent < 25;
                  return (
                    <div key={v.key} className="flex items-center gap-1.5">
                      <Heart className={cn("w-2.5 h-2.5", isLow ? "text-destructive" : "text-muted-foreground")} />
                      <div className="flex-1 h-1.5 rounded-full bg-muted/50 overflow-hidden">
                        <div
                          className={cn(
                            "h-full rounded-full transition-all",
                            isLow ? "bg-destructive" : v.key.includes("mana") || v.key === "mp" ? "bg-secondary" : "bg-primary",
                          )}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="text-[9px] tabular-nums text-muted-foreground w-8 text-right">{v.value}/{v.max}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Conditions */}
            {conditions.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {conditions.map((c: string) => (
                  <Badge key={c} variant="destructive" className="text-[9px] h-4 px-1">{c}</Badge>
                ))}
              </div>
            )}

            {/* No character data fallback */}
            {!cd.name && (
              <p className="text-[10px] text-muted-foreground/50 italic">{t.play_waiting_char}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
